import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RoutineStats } from '../../shared/interfaces/execution-plan.interfaces';
import { DateFormattingHelper } from '../../shared/helpers/date-formatting.helper';

export interface RoutineStatsModalData {
  routineName: string;
  stats: RoutineStats;
  timeRange: string;
}

/**
 * Status configuration for job statuses
 */
interface StatusConfig {
  label: string;
  icon: string;
  color: string;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  Completed: { label: 'Completed', icon: 'check_circle', color: 'success' },
  Failed: { label: 'Failed', icon: 'error', color: 'error' },
  Canceled: { label: 'Canceled', icon: 'cancel', color: 'warn' },
  Pending: { label: 'Pending', icon: 'schedule', color: 'accent' },
  Scheduled: { label: 'Scheduled', icon: 'event', color: 'primary' },
  Executing: { label: 'Executing', icon: 'play_circle', color: 'info' },
  Retry: { label: 'Retry', icon: 'replay', color: 'warn' },
  Aborted: { label: 'Aborted', icon: 'block', color: 'error' },
};

@Component({
  selector: 'app-routine-stats-modal',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatDividerModule,
    MatChipsModule,
    MatProgressBarModule,
    MatTooltipModule,
  ],
  templateUrl: './routine-stats-modal.component.html',
  styleUrls: ['./routine-stats-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoutineStatsModalComponent {
  private dialogRef = inject(MatDialogRef<RoutineStatsModalComponent>);
  public data = inject<RoutineStatsModalData>(MAT_DIALOG_DATA);

  onClose(): void {
    this.dialogRef.close();
  }

  get successRate(): number {
    if (this.data.stats.totalJobs === 0) return 0;
    return Math.round(
      (this.data.stats.completedJobs / this.data.stats.totalJobs) * 100,
    );
  }

  get failureRate(): number {
    if (this.data.stats.totalJobs === 0) return 0;
    return Math.round(
      (this.data.stats.failedJobs / this.data.stats.totalJobs) * 100,
    );
  }

  get avgDurationMinutes(): number {
    if (!this.data.stats.avgDurationMs) return 0;
    return Math.round((this.data.stats.avgDurationMs / 1000 / 60) * 100) / 100;
  }

  get avgDurationHours(): number {
    return Math.round((this.avgDurationMinutes / 60) * 100) / 100;
  }

  get statusDistribution(): Array<{
    status: string;
    count: number;
    percentage: number;
    color: string;
  }> {
    const total = this.data.stats.totalJobs;
    if (total === 0) return [];

    const statusMappings = [
      { key: 'Completed', count: this.data.stats.completedJobs },
      { key: 'Failed', count: this.data.stats.failedJobs },
      { key: 'Canceled', count: this.data.stats.canceledJobs },
      { key: 'Pending', count: this.data.stats.pendingJobs },
      { key: 'Scheduled', count: this.data.stats.scheduledJobs },
      { key: 'Executing', count: this.data.stats.executingJobs },
      { key: 'Retry', count: this.data.stats.retryJobs },
      { key: 'Aborted', count: this.data.stats.abortedJobs },
    ];

    return statusMappings
      .map(({ key, count }) => {
        const config = STATUS_CONFIG[key];
        return {
          status: config.label,
          count,
          percentage: Math.round((count / total) * 100),
          color: config.color,
        };
      })
      .filter((item) => item.count > 0);
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'Never';
    return DateFormattingHelper.formatDateTime(dateString);
  }

  getStatusIcon(status: string): string {
    return STATUS_CONFIG[status]?.icon || 'help';
  }

  getStatusColor(status: string): string {
    return STATUS_CONFIG[status]?.color || '';
  }
}
